import { fetchSignedIn } from '@/api/client';
import { useLens, lensKey } from '@/hooks/useTraderSets';
import { orderGroupQuery, type AssetOrder, type AssetOrderGroup, type AssetOrderGroups } from '@/lib/assetOrders.core';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import i18n from '@/i18n';
import type { AssetClass, Broker } from '@/lib/instruments';
import { transformTopTraders } from '@/hooks/useDashboardData';

/*
 * The Assets section's data (backend rest/assets_endpoints.go, 2026-10-04):
 * every asset CopyFi ever recorded a trade on, and one asset with its
 * description and what our traders did with it.
 */

export type AssetRow = {
  slug: string;
  display: string;
  class: AssetClass;
  name: string;
  logo: string;
  symbols: string[];
  canonical: string;
  brokers: Partial<Record<Broker, boolean>>;
  price: number | null;
  change_24h_pct: number | null;
  volume_24h: number | null;
  market_cap: number | null;
  price_source: string;
  traders: number;
  open_trades: number;
  closed_trades: number;
  recorded_volume: number;
  last_trade_at: string | null;
  /** What the listed traders made on it, closed trades (2026-10-05). */
  pnl: number;
  /** How they traded it, closed trades: the crowd's "usually" (2026-10-08). */
  closed_sides?: AssetCrowdSide;
};

/** One side of the asset page's crowd comparison (backend asset_crowd.go). */
export type AssetCrowdSide = {
  long: number;
  short: number;
  long_pct: number;
  avg_leverage: number;
  lev_n: number;
};

/** The listed traders' open positions on an asset against their closed
 *  trades, and the server's reading of the two (2026-10-08). */
export type AssetCrowd = {
  open: AssetCrowdSide;
  closed: AssetCrowdSide;
  long_shift_pts: number;
  leverage_ratio: number;
  reading: 'too_few' | 'in_line' | 'bullish' | 'strong_bullish' | 'bearish' | 'strong_bearish' | 'more_leverage' | 'less_leverage';
  rule: { min_open: number; min_closed: number; side_shift_pts: number; leverage_ratio: number };
};

/** A trader on one asset: the leaderboard's row (transformTopTraders) on
 *  that asset's trades only, so the leaderboard's columns read it. */
export type AssetTrader = ReturnType<typeof transformTopTraders>[number];

export type AssetDetail = {
  asset: AssetRow;
  crowd?: AssetCrowd;
  description: string;
  description_lang: string;
  source: string;
  source_url: string;
};

/** One page of an asset's trades or traders (50 a page, 2026-10-05). */
export type AssetPage<T> = { total: number; page: number; per_page: number } & T;

export function useAssets() {
  return useQuery<{ assets: AssetRow[]; ready: boolean }>({
    queryKey: ['assets'],
    queryFn: async () => {
      const r = await fetch('/api/v1/assets');
      if (!r.ok) throw new Error(`assets: ${r.status}`);
      return r.json();
    },
    // 432 KB for ~950 assets: once per 10 minutes, the server's own rebuild
    // pace, and not again every time the window regains focus.
    staleTime: 10 * 60_000,
    refetchInterval: 10 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

/** A plain GET, or, through a group's lens, one that says who is asking
 *  (the set must be the caller's own: backend trader_sets.go). */
async function assetGet<T>(path: string, setId: number | null, what: string): Promise<T> {
  if (setId != null) return fetchSignedIn<T>(`${path}${path.includes('?') ? '&' : '?'}set=${setId}`);
  const r = await fetch(path);
  if (!r.ok) throw new Error(`${what}: ${r.status}`);
  return r.json();
}

export function useAsset(slug: string | undefined) {
  const lang = (i18n.language || 'en').slice(0, 2);
  const { setId } = useLens();
  return useQuery<AssetDetail>({
    queryKey: ['asset', slug, lang, ...lensKey(setId)],
    queryFn: () => assetGet<AssetDetail>(`/api/v1/assets/${encodeURIComponent(slug ?? '')}?lang=${lang}`, setId, 'asset'),
    enabled: !!slug,
    staleTime: 60_000,
  });
}

/** The asset page a symbol belongs to, from the asset list. */
/** An asset's open or closed trades, a page at a time, in the trader
 *  profile's own row shape (the trade pop-up reads it). */
export function useAssetTrades(slug: string, status: 'open' | 'closed', page: number, sort = '', desc = false) {
  // An open table sorted by a column: the server sorts every row before the
  // page (asset_trades.go sortOpenTradeRows, 2026-10-08).
  const by = sort ? `&sort=${encodeURIComponent(sort)}&desc=${desc ? 1 : 0}` : '';
  const { setId } = useLens();
  return useQuery<AssetPage<{ trades: any[] }>>({
    queryKey: ['asset-trades', slug, status, page, sort, desc, ...lensKey(setId)],
    queryFn: () => assetGet<AssetPage<{ trades: any[] }>>(`/api/v1/assets/${encodeURIComponent(slug)}/trades?status=${status}&page=${page}${by}`, setId, 'asset trades'),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** The asset's traders a page at a time, sorted by a leaderboard column key
 *  (or "trader" for the name) on the server, which holds them all. */
export function useAssetTraders(slug: string, page: number, sort: string, desc: boolean) {
  const { setId } = useLens();
  return useQuery<AssetPage<{ traders: AssetTrader[] }>>({
    queryKey: ['asset-traders', slug, page, sort, desc, ...lensKey(setId)],
    queryFn: async () => {
      const body: any = await assetGet(`/api/v1/assets/${encodeURIComponent(slug)}/traders?page=${page}&sort=${encodeURIComponent(sort)}&desc=${desc ? 1 : 0}`, setId, 'asset traders');
      return { ...body, traders: transformTopTraders(body.traders ?? []) };
    },
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

/** Every trader of one asset, for the leaderboard's filters, which need the
 *  whole list, not a page of it (backend ?all=1). Fetched only while a
 *  filter is on. */
export function useAssetTradersAll(slug: string, enabled: boolean) {
  const { setId } = useLens();
  return useQuery<AssetPage<{ traders: AssetTrader[] }>>({
    queryKey: ['asset-traders-all', slug, ...lensKey(setId)],
    queryFn: async () => {
      const body: any = await assetGet(`/api/v1/assets/${encodeURIComponent(slug)}/traders?all=1`, setId, 'asset traders');
      return { ...body, traders: transformTopTraders(body.traders ?? []) };
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function assetSlugFor(symbol: string | null | undefined, assets: AssetRow[] | undefined): string | null {
  const s = String(symbol ?? '').toUpperCase();
  if (!s || !assets) return null;
  const hit = assets.find((a) => a.symbols.includes(s) || a.canonical === s);
  return hit ? hit.slug : null;
}

/* The asset page's order dots and its own news and odds (2026-10-09):
   backend rest/asset_orders.go and asset_insights.go. The dots are asked once
   the candles are up (enabled), so the line never waits on them. */
export function useAssetOrders(slug: string | undefined, from: number, to: number, interval: string, enabled: boolean) {
  const { setId } = useLens();
  return useQuery<AssetOrderGroups>({
    queryKey: ['asset-orders', slug, interval, from, ...lensKey(setId)],
    queryFn: () => assetGet<AssetOrderGroups>(`/api/v1/assets/${encodeURIComponent(slug ?? '')}/orders?from=${from}&to=${to}&interval=${interval}`, setId, 'asset orders'),
    enabled: !!slug && enabled,
    staleTime: 60_000,
  });
}

export function useAssetOrderGroup(slug: string | undefined, group: AssetOrderGroup | null, intervalSec: number) {
  const { setId } = useLens();
  return useQuery<{ orders: AssetOrder[] }>({
    queryKey: ['asset-order-group', slug, group?.t, group?.price_lo, group?.price_hi, ...lensKey(setId)],
    queryFn: () => assetGet<{ orders: AssetOrder[] }>(`/api/v1/assets/${encodeURIComponent(slug ?? '')}/orders?${orderGroupQuery(group as AssetOrderGroup, intervalSec)}`, setId, 'asset order group'),
    enabled: !!slug && !!group,
    staleTime: 60_000,
  });
}

export interface AssetInsights {
  news: { title: string; link: string; source: string; published_at: string }[];
  predictions: { question: string; yes_pct: number; volume: number; slug: string; source: string }[];
}

export function useAssetInsights(slug: string | undefined) {
  return useQuery<AssetInsights>({
    queryKey: ['asset-insights', slug],
    queryFn: async () => {
      const r = await fetch(`/api/v1/assets/${encodeURIComponent(slug ?? '')}/insights`);
      if (!r.ok) throw new Error(`asset insights: ${r.status}`);
      return r.json();
    },
    enabled: !!slug,
    staleTime: 5 * 60_000,
  });
}

